export type DiagramNodeProps = {
  x: number;
  y: number;
  width: number;
  height: number;
  rx?: number;
  label: string;
  sublabel?: string;
  eyebrow?: string;
  variant?:
    | "standard"
    | "capsule"
    | "llm"
    | "fallback"
    | "brass"
    | "repair"
    | "success";
  shape?: "rect" | "circle" | "double-circle";
  stroke?: string;
};

export function DiagramNode({
  x,
  y,
  width,
  height,
  rx = 8,
  label,
  sublabel,
  eyebrow,
  variant = "standard",
  shape = "rect",
  stroke,
}: DiagramNodeProps) {
  const isCapsule = variant === "capsule";
  const isRound = shape !== "rect";
  const cornerRadius = isCapsule ? height / 2 : rx;
  const radius = Math.min(width, height) / 2;
  const centerX = width / 2;
  const centerY = height / 2;
  const labelFontSize = isRound ? Math.min(14, Math.max(11, radius * 0.3)) : 14;
  const eyebrowFontSize = 9;
  const sublabelFontSize = 10;

  let fill = "var(--ga-paper-strong, #ffffff)";
  let strokeColor = stroke || "var(--ga-line, rgba(28,26,23,0.15))";
  let strokeWidth = "1.2";
  let strokeDasharray: string | undefined = undefined;

  if (variant === "llm") {
    fill =
      "color-mix(in srgb, var(--ga-diagram-agent) 7%, var(--ga-paper-strong))";
    strokeColor = "var(--ga-diagram-agent)";
    strokeWidth = "1.8";
    strokeDasharray = "6 4";
  } else if (variant === "fallback") {
    fill =
      "color-mix(in srgb, var(--ga-diagram-danger) 6%, var(--ga-paper-strong))";
    strokeColor = "var(--ga-diagram-danger)";
    strokeWidth = "1.5";
    strokeDasharray = "4 3";
  } else if (variant === "brass") {
    strokeColor = "var(--ga-brass, #9c6b3e)";
    strokeWidth = "1.5";
  } else if (variant === "repair") {
    fill =
      "color-mix(in srgb, var(--ga-diagram-repair) 7%, var(--ga-paper-strong))";
    strokeColor = "var(--ga-diagram-repair)";
    strokeWidth = "1.8";
  } else if (variant === "success") {
    strokeColor = "var(--ga-diagram-success)";
    strokeWidth = "1.8";
  }

  // Calculate balanced vertical text positions
  let eyebrowY = centerY - labelFontSize;
  let labelY = centerY;
  let sublabelY = centerY + labelFontSize;

  if (eyebrow && sublabel) {
    eyebrowY = centerY - height * 0.24;
    labelY = centerY + 1;
    sublabelY = centerY + height * 0.24;
  } else if (eyebrow && !sublabel) {
    eyebrowY = centerY - 10;
    labelY = centerY + 7;
  } else if (!eyebrow && sublabel) {
    labelY = centerY - 8;
    sublabelY = centerY + 9;
  }

  return (
    <g transform={`translate(${x}, ${y})`}>
      {isRound ? (
        <>
          <circle
            cx={centerX}
            cy={centerY}
            r={radius}
            fill={
              shape === "double-circle"
                ? `color-mix(in srgb, ${variant === "fallback" ? "var(--ga-diagram-danger)" : variant === "success" ? "var(--ga-diagram-success)" : "var(--ga-brass)"} 8%, var(--ga-paper-strong))`
                : fill
            }
            stroke={strokeColor}
            strokeWidth={strokeWidth}
            strokeDasharray={strokeDasharray}
          />
          {shape === "double-circle" && (
            <circle
              cx={centerX}
              cy={centerY}
              r={radius - 8}
              fill="var(--ga-paper-strong, #ffffff)"
              stroke={strokeColor}
              strokeWidth="2.3"
            />
          )}
        </>
      ) : (
        <rect
          width={width}
          height={height}
          rx={cornerRadius}
          fill={fill}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
          strokeDasharray={strokeDasharray}
        />
      )}
      {eyebrow && (
        <text
          x={centerX}
          y={eyebrowY}
          textAnchor="middle"
          dominantBaseline="middle"
          fill={variant === "llm" ? "var(--ga-diagram-agent)" : strokeColor}
          fontSize={eyebrowFontSize}
          className="font-mono font-bold tracking-wider uppercase"
        >
          {eyebrow}
        </text>
      )}
      <text
        x={centerX}
        y={labelY}
        textAnchor="middle"
        dominantBaseline="middle"
        fill={
          variant === "fallback" ? "var(--ga-diagram-danger)" : "currentColor"
        }
        fontSize={labelFontSize}
        className="font-bold"
      >
        {label}
      </text>
      {sublabel && (
        <text
          x={centerX}
          y={sublabelY}
          textAnchor="middle"
          dominantBaseline="middle"
          fontSize={sublabelFontSize}
          fill={
            variant === "fallback"
              ? "var(--ga-diagram-danger)"
              : variant === "success"
                ? "var(--ga-diagram-success)"
                : variant === "repair"
                  ? "var(--ga-diagram-repair)"
                  : "var(--ga-text-muted, rgba(28,26,23,0.7))"
          }
          className={`font-mono ${variant === "success" ? "font-semibold" : ""}`}
        >
          {sublabel}
        </text>
      )}
    </g>
  );
}
